import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Plus, MagnifyingGlass, Funnel, Export, Eye, PencilSimple, Trash, Phone, EnvelopeSimple, Paperclip, UploadSimple, DownloadSimple, FileText, Warning, FileXls, FilePdf, Sparkle, Question, CheckCircle, ShieldCheck, Clock, PauseCircle, Archive, Handshake, Users } from '@phosphor-icons/react';
import { PageHero } from '../components/motion/PageHero';
import { COLLEGES, findCollege } from '../constants/colleges';
import { getContacts, createContact, updateContact, deleteContact, getContactAttachments, uploadContactAttachment, downloadContactAttachment, deleteContactAttachment, getCustomFields, importContactsExcel, downloadContactsTemplate, mergeContact, getContact, checkContactDuplicates, lookupSirene, exportContacts } from '../services/api';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { DataTable } from '../components/DataTable';
import { PhoneInput } from '../components/PhoneInput';
import { Flag } from '../components/Flag';
import { MultiSelectChips } from '../components/MultiSelectChips';
import { NafAutocomplete } from '../components/NafAutocomplete';
import { ContactFicheStatus } from '../components/ContactFicheStatus';
import { TERRITOIRES, findTerritoire } from '../constants/countries';
import { TYPE_ACTEUR, findTypeActeur, SECTEURS, findSecteur } from '../constants/contactMeta';

const civiliteOptions = [
  { value: 'm', label: 'M.' },
  { value: 'mme', label: 'Mme' },
  { value: 'mx', label: 'Mx' },
];

const typeActeurOptions = TYPE_ACTEUR;

const statutJuridiqueOptions = [
  { value: 'association', label: 'Association' },
  { value: 'scic', label: 'SCIC' },
  { value: 'scop', label: 'SCOP' },
  { value: 'sarl', label: 'SARL' },
  { value: 'sas', label: 'SAS' },
  { value: 'eurl', label: 'EURL' },
  { value: 'ei', label: 'EI / Micro-entreprise' },
  { value: 'cooperative', label: 'Coopérative' },
  { value: 'mutuelle', label: 'Mutuelle' },
  { value: 'fondation', label: 'Fondation' },
  { value: 'collectivite', label: 'Collectivité' },
  { value: 'epci', label: 'EPCI' },
  { value: 'autre', label: 'Autre' },
];

const immatriculationTypeOptions = [
  { value: 'siret', label: 'SIRET' },
  { value: 'siren', label: 'SIREN' },
  { value: 'tva', label: 'TVA intracommunautaire' },
  { value: 'ridet', label: 'RIDET' },
  { value: 'autre', label: 'Immatriculation internationale' },
];

const typeCollectiviteOptions = [
  { value: 'ville', label: 'Ville / Commune' },
  { value: 'epci', label: 'EPCI' },
  { value: 'departement', label: 'Département' },
  { value: 'region', label: 'Région' },
];

const statutRelationOptions = [
  { value: 'nouveau',     label: 'Nouveau',     Icon: Sparkle,      iconColor: '#2563EB' },
  { value: 'a_qualifier', label: 'À qualifier', Icon: Question,     iconColor: '#B45309' },
  { value: 'qualifie',    label: 'Qualifié',    Icon: CheckCircle,  iconColor: '#16A34A' },
  { value: 'actif',       label: 'Actif',       Icon: ShieldCheck,  iconColor: '#059669' },
  { value: 'en_attente',  label: 'En attente',  Icon: Clock,        iconColor: '#EA580C' },
  { value: 'inactif',     label: 'Inactif',     Icon: PauseCircle,  iconColor: '#6B7280' },
  { value: 'archive',     label: 'Archivé',     Icon: Archive,      iconColor: '#475569' },
];
const findStatutRelation = (v) => statutRelationOptions.find(s => s.value === v);

const territoireOptions = TERRITOIRES;

const getStatutColor = (statut) => {
  const colors = {
    nouveau: 'bg-blue-100 text-blue-800',
    a_qualifier: 'bg-yellow-100 text-yellow-800',
    qualifie: 'bg-green-100 text-green-800',
    actif: 'bg-emerald-100 text-emerald-800',
    en_attente: 'bg-orange-100 text-orange-800',
    inactif: 'bg-gray-100 text-gray-800',
    archive: 'bg-slate-100 text-slate-800',
  };
  return colors[statut] || 'bg-gray-100 text-gray-800';
};

const getTypeColor = (type) => {
  const colors = {
    prospect: 'bg-[#2D5A4C]/10 text-[#2D5A4C]',
    membre: 'bg-[#8DB0A1]/20 text-[#2D5A4C]',
    partenaire: 'bg-[#E8A365]/20 text-[#E8A365]',
    prescripteur: 'bg-[#62A8D4]/20 text-[#62A8D4]',
    collectivite: 'bg-[#D47A62]/10 text-[#D47A62]',
    associe_cooperateur_pp: 'bg-[#2D5A4C]/15 text-[#2D5A4C]',
    associe_cooperateur_prestataire: 'bg-[#8DB0A1]/25 text-[#2D5A4C]',
    prestataire_tiers: 'bg-[#E8A365]/25 text-[#E8A365]',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
};

// Normalisation locale (aperçu en direct côté UI). Le backend applique la règle définitive.
const normalizePrenom = (v) => {
  if (!v) return v;
  return v.split(' ').map(part =>
    part.split('-').map(s => s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s).join('-')
  ).join(' ');
};
const normalizeNom = (v) => (v || '').toUpperCase();

// Format ISO YYYY-MM-DD <-> DD/MM/YYYY
const isoToFr = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return (y && m && d) ? `${d}/${m}/${y}` : iso;
};
const frToIso = (fr) => {
  if (!fr) return '';
  const m = fr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};
const todayIso = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

export function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTerritoire, setFilterTerritoire] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAutoCreated, setFilterAutoCreated] = useState(false);
  const [filterStatut, setFilterStatut] = useState('');
  const [filterStatutJuridique, setFilterStatutJuridique] = useState('');
  const [filterSecteur, setFilterSecteur] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [filterImmat, setFilterImmat] = useState(''); // '', 'with', 'without'
  const [filterRaisonSociale, setFilterRaisonSociale] = useState(''); // '', 'with', 'without'
  const [showFilters, setShowFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [saveError, setSaveError] = useState('');
  const savingContactRef = useRef(false);
  const initialContactFormRef = useRef(null);
  const replaceContact = contact => setContacts(items => items.map(item => item.id === contact.id ? contact : item));

  // Attachment state
  const [attachDialog, setAttachDialog] = useState({ open: false, contact: null });
  const [attachments, setAttachments] = useState([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachType, setAttachType] = useState('autre');
  const [attachDesc, setAttachDesc] = useState('');
  const fileInputRef = useRef(null);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [formData, setFormData] = useState({
    civilite: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    telephone_indicatif: '+33',
    fonction: '',
    territoire: '',
    type_acteur: 'prospect',
    statut_relation: 'nouveau',
    date_entree: todayIso(),
    adresse: '',
    adresse_quartier: '',
    adresse_batiment: '',
    adresse_porte: '',
    ville: '',
    code_postal: '',
    raison_sociale: '',
    statut_juridique: '',
    immatriculation_type: '',
    immatriculation_numero: '',
    type_collectivite: '',
    secteurs: [],
    code_naf: '',
    naf_label: '',
    college: '',
    date_naissance: '',
    custom_fields: {},
  });
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, dups: [], pending: null });
  const [mergeDialog, setMergeDialog] = useState({ open: false, existing: null, incoming: null, decisions: {} });
  const [liveSuggestions, setLiveSuggestions] = useState([]);
  const suggestDebRef = useRef(null);
  const [sireneState, setSireneState] = useState({ loading: false, message: '', tone: 'idle', autoTriggered: '' });
  const sireneDebRef = useRef(null);
  const structureDisabled = formData.type_acteur === 'associe_cooperateur_pp';
  const sireneRequestRef = useRef(0);
  const sireneAllowedRef = useRef(false);
  sireneAllowedRef.current = dialogOpen && !structureDisabled && !savingContact;
  const [importDialog, setImportDialog] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);

  const fetchContacts = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterTerritoire && filterTerritoire !== 'all') params.territoire = filterTerritoire;
      if (filterType && filterType !== 'all') params.type_acteur = filterType;
      if (filterStatut && filterStatut !== 'all') params.statut_relation = filterStatut;
      if (filterStatutJuridique && filterStatutJuridique !== 'all') params.statut_juridique = filterStatutJuridique;
      if (filterSecteur && filterSecteur !== 'all') params.secteur = filterSecteur;
      if (filterCollege && filterCollege !== 'all') params.college = filterCollege;
      if (filterImmat === 'with') params.has_immatriculation = true;
      if (filterImmat === 'without') params.has_immatriculation = false;
      if (filterRaisonSociale === 'with') params.has_raison_sociale = true;
      if (filterRaisonSociale === 'without') params.has_raison_sociale = false;
      if (filterAutoCreated) params.auto_created = true;

      const res = await getContacts(params);
      setContacts(res.data.items || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const res = await getCustomFields('contacts');
      setCustomFieldDefs(res.data.items || []);
    } catch {}
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterTerritoire && filterTerritoire !== 'all') params.territoire = filterTerritoire;
      if (filterType && filterType !== 'all') params.type_acteur = filterType;
      if (filterStatut && filterStatut !== 'all') params.statut_relation = filterStatut;
      if (filterStatutJuridique && filterStatutJuridique !== 'all') params.statut_juridique = filterStatutJuridique;
      if (filterSecteur && filterSecteur !== 'all') params.secteur = filterSecteur;
      if (filterCollege && filterCollege !== 'all') params.college = filterCollege;
      if (filterImmat === 'with') params.has_immatriculation = true;
      if (filterImmat === 'without') params.has_immatriculation = false;
      if (filterRaisonSociale === 'with') params.has_raison_sociale = true;
      if (filterRaisonSociale === 'without') params.has_raison_sociale = false;
      const res = await exportContacts(params);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchCustomFields();
  }, [search, filterTerritoire, filterType, filterStatut, filterStatutJuridique, filterSecteur, filterCollege, filterImmat, filterRaisonSociale, filterAutoCreated]);

  // Live suggestion pendant la saisie (email + telephone) — uniquement en creation
  useEffect(() => {
    if (editingContact) { setLiveSuggestions([]); return; }
    if (!dialogOpen) { setLiveSuggestions([]); return; }
    const email = formData.email?.trim() || '';
    const tel = formData.telephone?.trim() || '';
    const hasEmail = email.length >= 4 && email.includes('@');
    const hasTel = tel.replace(/\D/g, '').length >= 6;
    if (!hasEmail && !hasTel) { setLiveSuggestions([]); return; }
    if (suggestDebRef.current) clearTimeout(suggestDebRef.current);
    suggestDebRef.current = setTimeout(async () => {
      try {
        const params = {};
        if (hasEmail) params.email = email;
        if (hasTel) params.telephone = tel;
        const res = await checkContactDuplicates(params);
        setLiveSuggestions(res.data?.items || []);
      } catch {
        setLiveSuggestions([]);
      }
    }, 400);
    return () => suggestDebRef.current && clearTimeout(suggestDebRef.current);
  }, [formData.email, formData.telephone, dialogOpen, editingContact]);

  // Enrichissement SIRENE : recuperation auto raison sociale / NAF / adresse a partir du SIRET
  const isValidSiretLuhn = (raw) => {
    const v = (raw || '').replace(/\D/g, '');
    if (v.length !== 14) return false;
    let sum = 0;
    for (let i = v.length - 1, j = 0; i >= 0; i--, j++) {
      let n = parseInt(v[i], 10);
      if (j % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
      sum += n;
    }
    return sum % 10 === 0;
  };

  const applySireneToForm = (data) => {
    setFormData((prev) => {
      if (prev.type_acteur === 'associe_cooperateur_pp') return prev;
      const next = { ...prev };
      const setIfEmpty = (key, val) => {
        if (val && (next[key] === '' || next[key] == null)) next[key] = val;
      };
      setIfEmpty('raison_sociale', data.denomination);
      setIfEmpty('code_naf', data.code_naf);
      setIfEmpty('naf_label', data.naf_label);
      setIfEmpty('adresse', data.adresse);
      setIfEmpty('code_postal', data.code_postal);
      setIfEmpty('ville', data.ville);
      return next;
    });
  };

  const triggerSireneLookup = async ({ silent = false } = {}) => {
    if (!sireneAllowedRef.current) return;
    const raw = (formData.immatriculation_numero || '').replace(/\D/g, '');
    if (!isValidSiretLuhn(raw)) {
      if (!silent) setSireneState({ loading: false, message: 'SIRET invalide (14 chiffres + clé de Luhn)', tone: 'error', autoTriggered: '' });
      return;
    }
    const requestId = ++sireneRequestRef.current;
    setSireneState((s) => ({ ...s, loading: true, message: 'Recherche INSEE Sirene…', tone: 'loading' }));
    try {
      const res = await lookupSirene(raw);
      if (!sireneAllowedRef.current || requestId !== sireneRequestRef.current) return;
      const data = res.data || {};
      applySireneToForm(data);
      const filled = [];
      if (data.denomination) filled.push('raison sociale');
      if (data.code_naf) filled.push('NAF');
      if (data.adresse || data.code_postal || data.ville) filled.push('adresse');
      const suffix = filled.length ? ` — ${filled.join(', ')} récupéré(s)` : '';
      const closedTag = data.ferme ? ' ⚠️ établissement fermé' : '';
      setSireneState({ loading: false, message: `${data.denomination || raw}${suffix}${closedTag}`, tone: data.ferme ? 'warning' : 'success', autoTriggered: raw });
      if (!silent) toast.success(`SIRENE : ${data.denomination || raw}${closedTag}`);
    } catch (err) {
      if (!sireneAllowedRef.current || requestId !== sireneRequestRef.current) return;
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Recherche SIRENE échouée';
      setSireneState({ loading: false, message: msg, tone: 'error', autoTriggered: '' });
      if (!silent) toast.error(msg);
    }
  };

  // Auto-declenchement : quand le type=siret et le numero devient un SIRET valide, on interroge SIRENE
  useEffect(() => {
    if (structureDisabled || !dialogOpen) return;
    if (editingContact) return; // pas d'ecrasement automatique en edition
    if (formData.immatriculation_type !== 'siret') return;
    const raw = (formData.immatriculation_numero || '').replace(/\D/g, '');
    if (!isValidSiretLuhn(raw)) return;
    if (sireneState.autoTriggered === raw) return; // deja fait pour ce SIRET
    if (sireneDebRef.current) clearTimeout(sireneDebRef.current);
    sireneDebRef.current = setTimeout(() => {
      triggerSireneLookup({ silent: true });
    }, 500);
    return () => sireneDebRef.current && clearTimeout(sireneDebRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.immatriculation_type, formData.immatriculation_numero, editingContact, structureDisabled, dialogOpen]);

  // Reset SIRENE status when dialog opens/closes or immat type leaves 'siret'
  useEffect(() => {
    sireneRequestRef.current += 1;
    if (structureDisabled || !dialogOpen || formData.immatriculation_type !== 'siret') {
      setSireneState({ loading: false, message: '', tone: 'idle', autoTriggered: '' });
    }
  }, [dialogOpen, formData.immatriculation_type, structureDisabled]);

  const defaultForm = () => ({
    civilite: '', nom: '', prenom: '', email: '',
    telephone: '', telephone_indicatif: '+33',
    fonction: '', territoire: '',
    type_acteur: 'prospect', statut_relation: 'nouveau',
    date_entree: todayIso(),
    adresse: '', adresse_quartier: '', adresse_batiment: '', adresse_porte: '',
    ville: '', code_postal: '',
    raison_sociale: '', statut_juridique: '',
    immatriculation_type: '', immatriculation_numero: '',
    type_collectivite: '',
    secteurs: [],
    code_naf: '',
    naf_label: '',
    college: '',
    date_naissance: '',
    custom_fields: {},
  });

  const submitCreate = async (payload, { force = false } = {}) => {
    try {
      await createContact(payload, { force });
      toast.success('Contact créé');
      setDialogOpen(false);
      setEditingContact(null);
      setFormData(defaultForm());
      setDuplicateDialog({ open: false, dups: [], pending: null });
      fetchContacts();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate') {
        setDuplicateDialog({ open: true, dups: detail.duplicates || [], pending: payload });
      } else if (status === 409 && detail && detail.code === 'ridet_duplicate') {
        toast.error(detail.message || 'RIDET déjà utilisé');
      } else if (status === 422 && detail && detail.code === 'invalid_immatriculation') {
        toast.error(detail.message || 'Immatriculation invalide');
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Erreur'));
      }
    }
  };

  const submitMerge = async (targetId, payload) => {
    try {
      const res = await mergeContact(targetId, payload);
      const n = res.data?.merged || 0;
      toast.success(n > 0 ? `Fusion : ${n} champ(s) complétés` : 'Aucun champ à fusionner');
      setDuplicateDialog({ open: false, dups: [], pending: null });
      setDialogOpen(false);
      setEditingContact(null);
      setFormData(defaultForm());
      fetchContacts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Erreur fusion'));
    }
  };

  const MERGE_FIELDS = [
    { key: 'civilite',                label: 'Civilité' },
    { key: 'prenom',                  label: 'Prénom' },
    { key: 'nom',                     label: 'Nom' },
    { key: 'fonction',                label: 'Fonction' },
    { key: 'email',                   label: 'Email' },
    { key: 'telephone_indicatif',     label: 'Indicatif tél.' },
    { key: 'telephone',               label: 'Téléphone' },
    { key: 'territoire',              label: 'Territoire' },
    { key: 'pays',                    label: 'Pays' },
    { key: 'type_acteur',             label: "Type d'acteur" },
    { key: 'statut_relation',         label: 'Statut relation' },
    { key: 'date_entree',             label: "Date d'entrée" },
    { key: 'raison_sociale',          label: 'Raison sociale' },
    { key: 'statut_juridique',        label: 'Statut juridique' },
    { key: 'immatriculation_type',    label: 'Type immat.' },
    { key: 'immatriculation_numero',  label: 'N° immat.' },
    { key: 'type_collectivite',       label: 'Type collectivité' },
    { key: 'secteurs',                label: "Secteurs d'activité" },
  ];
  const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
  const fmtMergeValue = (v) => Array.isArray(v) ? (v.length ? v.join(', ') : '—') : (isEmpty(v) ? '—' : String(v));

  const openMergeDialog = async (existingId, incoming) => {
    try {
      const res = await getContact(existingId);
      const existing = res.data;
      // Decisions par defaut : conserver l'existant sauf si champ vide et incoming non vide
      const decisions = {};
      MERGE_FIELDS.forEach(f => {
        const ev = existing[f.key], iv = incoming[f.key];
        if (isEmpty(ev) && !isEmpty(iv)) decisions[f.key] = 'new';
        else decisions[f.key] = 'existing';
      });
      setDuplicateDialog({ open: false, dups: [], pending: null });
      setMergeDialog({ open: true, existing, incoming, decisions });
    } catch {
      toast.error('Impossible de charger le contact existant');
    }
  };

  const applyMergeDecisions = async () => {
    const { existing, incoming, decisions } = mergeDialog;
    if (!existing) return;
    const patch = {};
    MERGE_FIELDS.forEach(f => {
      if (decisions[f.key] === 'new' && !isEmpty(incoming[f.key])) {
        patch[f.key] = incoming[f.key];
      }
    });
    if (Object.keys(patch).length === 0) {
      toast.info('Aucun changement sélectionné');
      setMergeDialog({ open: false, existing: null, incoming: null, decisions: {} });
      return;
    }
    try {
      await updateContact(existing.id, patch);
      toast.success(`Fusion appliquée : ${Object.keys(patch).length} champ(s) modifiés`);
      setMergeDialog({ open: false, existing: null, incoming: null, decisions: {} });
      setDialogOpen(false);
      setEditingContact(null);
      setFormData(defaultForm());
      fetchContacts();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Erreur fusion'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingContactRef.current) return;
    savingContactRef.current = true;
    setSavingContact(true); setSaveError('');
    sireneRequestRef.current += 1;
    try {
      if (editingContact) {
        const patch = Object.fromEntries(Object.entries(formData).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(initialContactFormRef.current?.[key])));
        if (Object.keys(patch).length) {
          const response = await updateContact(editingContact.id, patch);
          replaceContact(response.data);
          toast.success(response.data.fiche_status === 'pending' ? 'Contact mis à jour. Fiche PDF en préparation.' : 'Contact mis à jour');
        } else {
          toast.info('Aucune modification à enregistrer');
        }
        setDialogOpen(false);
        setEditingContact(null);
        setFormData(defaultForm());
        fetchContacts();
      } else {
        await submitCreate(formData, { force: false });
      }
    } catch (error) {
      const message = error.response ? formatApiError(error, 'Impossible d’enregistrer le contact.') : 'Confirmation non reçue. Vos saisies sont conservées ; vérifiez la fiche avant de réessayer.';
      setSaveError(message); toast.error(message);
    } finally {
      savingContactRef.current = false;
      setSavingContact(false);
    }
  };

  const handleEdit = (contact) => {
    if (savingContactRef.current) return;
    setSaveError('');
    setEditingContact(contact);
    const initial = {
      civilite: contact.civilite || '',
      nom: contact.nom || '',
      prenom: contact.prenom || '',
      email: contact.email || '',
      telephone: contact.telephone || '',
      telephone_indicatif: contact.telephone_indicatif || '+33',
      fonction: contact.fonction || '',
      territoire: contact.territoire || '',
      type_acteur: contact.type_acteur || 'prospect',
      statut_relation: contact.statut_relation || 'nouveau',
      date_entree: contact.date_entree || todayIso(),
      adresse: contact.adresse || '',
      adresse_quartier: contact.adresse_quartier || '',
      adresse_batiment: contact.adresse_batiment || '',
      adresse_porte: contact.adresse_porte || '',
      ville: contact.ville || '',
      code_postal: contact.code_postal || '',
      raison_sociale: contact.raison_sociale || '',
      statut_juridique: contact.statut_juridique || '',
      immatriculation_type: contact.immatriculation_type || '',
      immatriculation_numero: contact.immatriculation_numero || '',
      type_collectivite: contact.type_collectivite || '',
      secteurs: contact.secteurs || [],
      code_naf: contact.code_naf || '',
      naf_label: contact.naf_label || '',
      college: contact.college || '',
      date_naissance: contact.date_naissance || '',
      custom_fields: contact.custom_fields || {},
    };
    initialContactFormRef.current = JSON.parse(JSON.stringify(initial));
    setFormData(initial);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) {
      try {
        await deleteContact(id);
        toast.success('Contact supprimé');
        fetchContacts();
      } catch (error) {
        toast.error(formatApiError(error, 'Erreur lors de la suppression'));
      }
    }
  };

  const openAttachments = async (contact) => {
    setAttachDialog({ open: true, contact });
    setAttachLoading(true);
    try {
      const res = await getContactAttachments(contact.id);
      setAttachments(res.data.items || []);
    } catch {} finally { setAttachLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const contactId = attachDialog.contact?.id;
    if (!contactId) return;

    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type_piece', attachType);
      fd.append('description', attachDesc);
      await uploadContactAttachment(contactId, fd);
      toast.success('Piece jointe ajoutee');
      setAttachType('autre');
      setAttachDesc('');
      const res = await getContactAttachments(contactId);
      setAttachments(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      const res = await downloadContactAttachment(attachDialog.contact.id, att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = att.original_filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur telechargement'); }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm('Supprimer cette piece jointe ?')) return;
    try {
      await deleteContactAttachment(attachDialog.contact.id, att.id);
      toast.success('Piece jointe supprimee');
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch { toast.error('Erreur suppression'); }
  };

  const pieceTypeLabels = {
    cv: 'CV', piece_identite: 'Piece identite', rib: 'RIB', contrat: 'Contrat', attestation: 'Attestation', autre: 'Autre',
  };

  const openNewDialog = () => {
    if (savingContactRef.current) return;
    setSaveError('');
    setEditingContact(null);
    setFormData(defaultForm());
    setDialogOpen(true);
  };

  // Keyboard shortcut: 'n' opens new dialog
  useEffect(() => {
    const handleNew = () => {
      if (savingContactRef.current || document.querySelector('[data-testid="contact-form-dialog"]')) return;
      setSaveError('');
      setEditingContact(null);
      setFormData(defaultForm());
      setDialogOpen(true);
    };
    window.addEventListener('crm-shortcut-new', handleNew);
    return () => window.removeEventListener('crm-shortcut-new', handleNew);
  }, []);

  const handleImportTemplate = async () => {
    try {
      const res = await downloadContactsTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'import_contacts_template.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur téléchargement du template');
    }
  };

  const handleImportSubmit = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('skip_duplicates', 'true');
      const res = await importContactsExcel(fd);
      setImportResult(res.data);
      toast.success(`${res.data.inserted} contact(s) importé(s), ${res.data.skipped} ignoré(s)`);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Contacts"
        subtitle="Gérez vos contacts, prospects et coopérateurs — tous territoires confondus"
        icon={<Users size={24} weight="duotone" className="text-white" />}
        gradient="from-[#00AEEF] to-[#2D5A4C]"
        accents={['#80C51F', '#FF8E00']}
        actions={(
          <>
            <Button onClick={() => setImportDialog(true)} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="import-excel-btn">
              <FileXls size={18} className="mr-2" weight="fill" />
              Importer
            </Button>
            <Button onClick={openNewDialog} className="bg-[#FF8E00] text-white hover:bg-[#e07d00]" data-testid="new-contact-btn">
              <Plus size={18} className="mr-2" />
              Nouveau
            </Button>
          </>
        )}
      />

      {/* Search and Filters */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
              <Input
                type="search"
                placeholder="Rechercher par nom, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E8E6E1]"
                data-testid="contact-search"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="border-[#E8E6E1]"
              data-testid="toggle-filters"
            >
              <Funnel size={18} className="mr-2" />
              Filtres
            </Button>
            <Button variant="outline" className="border-[#E8E6E1]" onClick={handleExport} data-testid="export-contacts-btn">
              <Export size={18} className="mr-2" />
              Exporter
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-[#E8E6E1]">
              <Select value={filterTerritoire} onValueChange={setFilterTerritoire}>
                <SelectTrigger className="w-[170px] border-[#E8E6E1]" data-testid="filter-territoire">
                  <SelectValue placeholder="Territoire" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {territoireOptions.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[190px] border-[#E8E6E1]" data-testid="filter-type">
                  <SelectValue placeholder="Type d'acteur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {typeActeurOptions.map(t => {
                    const I = t.Icon;
                    return (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="flex items-center gap-2">
                          <I size={14} weight="fill" style={{ color: t.color }} />
                          <span>{t.label}</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              <Select value={filterStatut} onValueChange={setFilterStatut}>
                <SelectTrigger className="w-[170px] border-[#E8E6E1]" data-testid="filter-statut">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {statutRelationOptions.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatutJuridique} onValueChange={setFilterStatutJuridique}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]" data-testid="filter-statut-juridique">
                  <SelectValue placeholder="Statut juridique" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {statutJuridiqueOptions.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterSecteur} onValueChange={setFilterSecteur}>
                <SelectTrigger className="w-[200px] border-[#E8E6E1]" data-testid="filter-secteur">
                  <SelectValue placeholder="Secteur d'activité" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">Tous</SelectItem>
                  {SECTEURS.map(s => (
                    <SelectItem key={s.value} value={s.value} data-testid={`filter-secteur-opt-${s.value}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterCollege} onValueChange={setFilterCollege}>
                <SelectTrigger className="w-[220px] border-[#E8E6E1]" data-testid="filter-college">
                  <SelectValue placeholder="Collège coopératif" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les collèges</SelectItem>
                  {COLLEGES.map(c => (
                    <SelectItem key={c.value} value={c.value} data-testid={`filter-college-${c.value}`}>
                      <span className="flex items-center gap-2">
                        <c.Icon size={13} style={{ color: c.color }} />
                        <span className="font-medium">{c.value}</span>
                        <span className="text-xs text-[#5E6D66]">— {c.label} ({c.ponderation}%)</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterImmat} onValueChange={setFilterImmat}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]" data-testid="filter-immat">
                  <SelectValue placeholder="Immatriculation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="with">Avec immatriculation</SelectItem>
                  <SelectItem value="without">Sans immatriculation</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterRaisonSociale} onValueChange={setFilterRaisonSociale}>
                <SelectTrigger className="w-[180px] border-[#E8E6E1]" data-testid="filter-raison-sociale">
                  <SelectValue placeholder="Structure" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="with">Avec raison sociale</SelectItem>
                  <SelectItem value="without">Sans raison sociale</SelectItem>
                </SelectContent>
              </Select>

              <button
                type="button"
                onClick={() => setFilterAutoCreated(!filterAutoCreated)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                  filterAutoCreated
                    ? 'bg-[#FF8E00] text-white border-[#FF8E00] shadow-md'
                    : 'bg-white text-[#5E6D66] border-[#E8E6E1] hover:border-[#FF8E00] hover:text-[#FF8E00]'
                }`}
                data-testid="filter-auto-created"
                title="Afficher uniquement les contacts créés automatiquement lors d'inscriptions publiques, en attente de complétion par un super admin"
              >
                <Sparkle size={13} weight={filterAutoCreated ? 'fill' : 'bold'} />
                À compléter
              </button>

              {(filterTerritoire || filterType || filterStatut || filterStatutJuridique || filterSecteur || filterCollege || filterImmat || filterRaisonSociale || filterAutoCreated) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#5E6D66]"
                  onClick={() => {
                    setFilterTerritoire(''); setFilterType(''); setFilterStatut('');
                    setFilterStatutJuridique(''); setFilterSecteur(''); setFilterCollege('');
                    setFilterImmat(''); setFilterRaisonSociale(''); setFilterAutoCreated(false);
                  }}
                  data-testid="filter-reset"
                >
                  Réinitialiser
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5E6D66]">Aucun contact trouve</p>
              <Button onClick={openNewDialog} variant="link" className="text-[#2D5A4C] mt-2">
                Creer votre premier contact
              </Button>
            </div>
          ) : (
            <DataTable
              data={contacts}
              columns={[
                {
                  key: 'nom',
                  label: 'Contact',
                  render: (_, row) => (
                    <div>
                      <p className="font-medium text-[#1A2421]">{row.prenom} {row.nom}</p>
                      {row.fonction && <p className="text-sm text-[#5E6D66]">{row.fonction}</p>}
                      <ContactFicheStatus contact={row} onChange={replaceContact} />
                    </div>
                  ),
                },
                {
                  key: 'email',
                  label: 'Coordonnees',
                  render: (_, row) => (
                    <div className="space-y-1">
                      <p className="text-sm text-[#5E6D66] flex items-center gap-1"><EnvelopeSimple size={14} /> {row.email}</p>
                      {row.telephone && <p className="text-sm text-[#5E6D66] flex items-center gap-1"><Phone size={14} /> {row.telephone}</p>}
                    </div>
                  ),
                },
                {
                  key: 'territoire',
                  label: 'Territoire',
                  render: (val) => {
                    if (!val) return '-';
                    const t = findTerritoire(val);
                    return (
                      <Badge variant="outline" className="border-[#E8E6E1] gap-1.5">
                        <Flag iso={t?.iso} size={14} />
                        <span>{val}</span>
                      </Badge>
                    );
                  },
                },
                {
                  key: 'type_acteur',
                  label: 'Type',
                  render: (val) => {
                    const t = findTypeActeur(val);
                    const I = t?.Icon;
                    return (
                      <Badge className={`${getTypeColor(val)} gap-1.5`}>
                        {I && <I size={12} weight="fill" style={{ color: t.color }} />}
                        {t?.label || val}
                      </Badge>
                    );
                  },
                },
                {
                  key: 'date_naissance',
                  label: 'Naissance',
                  render: (val) => <span className="text-xs text-[#5E6D66] tabular-nums">{isoToFr(val) || '—'}</span>,
                },
                {
                  key: 'college',
                  label: 'Collège',
                  render: (val) => {
                    const c = findCollege(val);
                    if (!c) return <span className="text-xs text-[#5E6D66]">—</span>;
                    const I = c.Icon;
                    return (
                      <Badge variant="outline" className="gap-1" style={{ borderColor: c.color, color: c.color }} title={`${c.label} (${c.ponderation}%)`}>
                        <I size={11} />{c.value}
                      </Badge>
                    );
                  },
                },
                {
                  key: 'statut_relation',
                  label: 'Statut',
                  render: (val) => {
                    const s = findStatutRelation(val);
                    const I = s?.Icon;
                    return (
                      <Badge className={`${getStatutColor(val)} gap-1.5`}>
                        {I && <I size={12} weight="fill" style={{ color: s.iconColor }} />}
                        {s?.label || val}
                      </Badge>
                    );
                  },
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  sortable: false,
                  className: 'text-right',
                  cellClassName: 'text-right',
                  render: (_, row) => (
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {row.ged_fiche_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${row.ged_fiche_id}/view`, '_blank')}
                          title={row.fiche_status && row.fiche_status !== 'ready' ? 'Voir la dernière fiche archivée (nouvelle version en préparation)' : 'Voir la fiche PDF'}
                          data-testid={`contact-view-fiche-${row.id}`}
                        >
                          <FilePdf size={16} className="text-[#2D5A4C]" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openAttachments(row)} title="Pieces jointes" data-testid={`attachments-contact-${row.id}`}>
                        <Paperclip size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/contacts/${row.id}`)} data-testid={`view-contact-${row.id}`}>
                        <Eye size={16} />
                      </Button>
                      {row.email && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={async () => {
                            if (!window.confirm(`Envoyer un kit d'adhésion à ${row.email} ?`)) return;
                            try {
                              const { sendAdhesionKit } = await import('../services/api');
                              await sendAdhesionKit({ contact_id: row.id, kit_type: 'associe_pp' });
                              toast.success('Kit PDF transmis au service email');
                            } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur envoi'); }
                          }}
                          title="Envoyer kit d'adhésion"
                          data-testid={`send-kit-contact-${row.id}`}
                        >
                          <Handshake size={16} className="text-[#2D5A4C]" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} data-testid={`edit-contact-${row.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-[#D47A62]" data-testid={`delete-contact-${row.id}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  ),
                },
              ]}
              searchKeys={['nom', 'prenom', 'email', 'telephone', 'fonction']}
              searchPlaceholder="Filtrer dans les resultats..."
              showSearch={false}
              emptyMessage="Aucun contact trouve"
              data-testid="contacts-datatable"
              mobileCardRender={(row) => (
                <Card className="min-w-0 border-[#E8E6E1] hover:border-[#2D5A4C]/30 transition-colors active:bg-[#F9F8F5]">
                  <CardContent className="p-3">
                    <div className="flex min-w-0 flex-col gap-2">
                      <div className="min-w-0 w-full">
                        <p className="text-sm font-semibold text-[#1A2421] break-words" data-testid={`mobile-contact-name-${row.id}`}>{row.prenom} {row.nom}</p>
                        {row.fonction && <p className="text-[11px] text-[#5E6D66] break-words" data-testid={`mobile-contact-function-${row.id}`}>{row.fonction}</p>}
                        <ContactFicheStatus contact={row} onChange={replaceContact} prefix="mobile-contact" />
                        <p className="min-w-0 text-[11px] text-[#5E6D66] mt-1 flex items-start gap-1" data-testid={`mobile-contact-email-${row.id}`}><EnvelopeSimple size={12} className="shrink-0 mt-0.5" /><span className="min-w-0 break-all">{row.email}</span></p>
                        {row.telephone && <p className="min-w-0 text-[11px] text-[#5E6D66] flex items-start gap-1" data-testid={`mobile-contact-phone-${row.id}`}><Phone size={12} className="shrink-0 mt-0.5" /><span className="min-w-0 break-all">{row.telephone}</span></p>}
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-1">
                        <Badge className={`max-w-full whitespace-normal text-[9px] ${getTypeColor(row.type_acteur)}`} data-testid={`mobile-contact-type-${row.id}`}>{typeActeurOptions.find(t => t.value === row.type_acteur)?.label || row.type_acteur}</Badge>
                        <Badge className={`max-w-full whitespace-normal text-[9px] ${getStatutColor(row.statut_relation)}`} data-testid={`mobile-contact-status-${row.id}`}>{statutRelationOptions.find(s => s.value === row.statut_relation)?.label || row.statut_relation}</Badge>
                        {row.territoire && <Badge variant="outline" className="max-w-full whitespace-normal text-[9px] border-[#E8E6E1]" data-testid={`mobile-contact-territory-${row.id}`}>{row.territoire}</Badge>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mt-2 pt-2 border-t border-[#F0EFEB]" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid={`mobile-view-contact-${row.id}`} onClick={() => navigate(`/contacts/${row.id}`)}>Voir</Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid={`mobile-edit-contact-${row.id}`} onClick={() => handleEdit(row)}>Modifier</Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-[#D47A62]" data-testid={`mobile-delete-contact-${row.id}`} onClick={() => handleDelete(row.id)}>Supprimer</Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={open => { if (!savingContactRef.current) setDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" showCloseButton={!savingContact} data-testid="contact-form-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingContact ? 'Modifier le contact' : 'Nouveau contact'}
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" aria-busy={savingContact} data-testid="contact-form">
            <fieldset disabled={savingContact} className="min-w-0 space-y-4" data-testid="contact-save-fields">
            {/* Suggestion Auto — bandeau doublons potentiels en temps reel */}
            {!editingContact && liveSuggestions.length > 0 && (
              <div className="p-3 rounded-lg bg-[#E8A365]/10 border border-[#E8A365]/40 flex items-start gap-3" data-testid="live-suggestions">
                <Warning size={18} weight="fill" className="text-[#E8A365] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1A2421] mb-1">
                    {liveSuggestions.length} contact(s) similaire(s) déjà en base
                  </div>
                  <div className="flex flex-col gap-1">
                    {liveSuggestions.slice(0, 3).map(s => (
                      <div key={s.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`live-suggestion-${s.id}`}>
                        <span className="text-[#1A2421] truncate">
                          <span className="font-medium">{s.prenom} {s.nom}</span>
                          <span className="text-[#5E6D66]"> · {s.email}{s.telephone ? ` · ${s.telephone}` : ''}</span>
                        </span>
                        <button
                          type="button"
                          className="text-[#2D5A4C] hover:underline flex-shrink-0"
                          onClick={() => {
                            setDialogOpen(false);
                            navigate(`/contacts/${s.id}`);
                          }}
                          data-testid={`live-suggestion-open-${s.id}`}
                        >
                          Ouvrir
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-[110px_1fr_1fr] gap-4">
              <div className="space-y-2">
                <Label htmlFor="civilite">Civilité</Label>
                <Select value={formData.civilite} onValueChange={(v) => setFormData({ ...formData, civilite: v })}>
                  <SelectTrigger data-testid="select-civilite">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {civiliteOptions.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  onBlur={(e) => setFormData({ ...formData, prenom: normalizePrenom(e.target.value) })}
                  required
                  data-testid="input-prenom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: normalizeNom(e.target.value) })}
                  required
                  data-testid="input-nom"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="input-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <PhoneInput
                  indicatif={formData.telephone_indicatif}
                  number={formData.telephone}
                  onChange={({ indicatif, number }) => setFormData({ ...formData, telephone_indicatif: indicatif, telephone: number })}
                  testIdPrefix="contact-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fonction">Fonction</Label>
                <Input
                  id="fonction"
                  value={formData.fonction}
                  onChange={(e) => setFormData({ ...formData, fonction: e.target.value })}
                  data-testid="input-fonction"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="territoire">Territoire</Label>
                <Select value={formData.territoire} onValueChange={(v) => {
                  const t = findTerritoire(v);
                  setFormData(prev => ({
                    ...prev,
                    territoire: v,
                    pays: v,
                    telephone_indicatif: (!prev.telephone && t?.dial) ? t.dial : prev.telephone_indicatif,
                  }));
                }}>
                  <SelectTrigger data-testid="select-territoire">
                    <SelectValue placeholder="Sélectionner un territoire">
                      {formData.territoire ? (
                        <span className="flex items-center gap-2">
                          <Flag iso={findTerritoire(formData.territoire)?.iso} size={16} />
                          <span>{formData.territoire}</span>
                        </span>
                      ) : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {territoireOptions.map(t => (
                      <SelectItem key={t.value} value={t.value} data-testid={`territoire-opt-${t.value}`}>
                        <span className="flex items-center gap-2">
                          <Flag iso={t.iso} size={16} />
                          <span>{t.label}</span>
                          {t.dial ? <span className="text-xs text-[#5E6D66]">{t.dial}</span> : null}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_entree">Date d'entrée</Label>
                <Input
                  id="date_entree"
                  type="text"
                  inputMode="numeric"
                  placeholder="JJ/MM/AAAA"
                  value={isoToFr(formData.date_entree)}
                  onChange={(e) => {
                    // Auto-formatage JJ/MM/AAAA
                    let v = e.target.value.replace(/[^\d/]/g, '');
                    if (v.length === 2 || v.length === 5) if (!v.endsWith('/')) v += '/';
                    if (v.length > 10) v = v.slice(0, 10);
                    const iso = frToIso(v);
                    setFormData({ ...formData, date_entree: iso || v });
                  }}
                  onBlur={(e) => {
                    const iso = frToIso(e.target.value);
                    if (iso) setFormData({ ...formData, date_entree: iso });
                  }}
                  data-testid="input-date-entree"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_naissance">Date de naissance</Label>
                <Input
                  id="date_naissance"
                  type="text"
                  inputMode="numeric"
                  placeholder="JJ/MM/AAAA"
                  value={isoToFr(formData.date_naissance)}
                  onChange={(e) => {
                    let v = e.target.value.replace(/[^\d/]/g, '');
                    if (v.length === 2 || v.length === 5) if (!v.endsWith('/')) v += '/';
                    if (v.length > 10) v = v.slice(0, 10);
                    const iso = frToIso(v);
                    setFormData({ ...formData, date_naissance: iso || v });
                  }}
                  onBlur={(e) => {
                    const iso = frToIso(e.target.value);
                    if (iso) setFormData({ ...formData, date_naissance: iso });
                  }}
                  data-testid="input-date-naissance"
                />
              </div>
              <div className="space-y-2">
                <Label>Collège coopératif</Label>
                <Select value={formData.college} onValueChange={(v) => setFormData({ ...formData, college: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="input-college">
                    <SelectValue placeholder="Sélectionner">
                      {formData.college ? (() => {
                        const c = findCollege(formData.college);
                        return c ? <span className="flex items-center gap-2"><c.Icon size={14} style={{ color: c.color }} /><span className="font-medium">{c.value}</span><span className="text-[#5E6D66]">— {c.label} ({c.ponderation}%)</span></span> : null;
                      })() : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucun —</SelectItem>
                    {COLLEGES.map(c => (
                      <SelectItem key={c.value} value={c.value} data-testid={`input-college-${c.value}`}>
                        <span className="flex items-center gap-2">
                          <c.Icon size={14} style={{ color: c.color }} />
                          <span className="font-semibold">{c.value}</span>
                          <span>— {c.label}</span>
                          <span className="text-[#5E6D66] text-xs">({c.ponderation}%)</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type_acteur">Type d'acteur</Label>
                <Select value={formData.type_acteur} onValueChange={(v) => setFormData({ ...formData, type_acteur: v })}>
                  <SelectTrigger data-testid="select-type-acteur">
                    <SelectValue>
                      {(() => {
                        const t = findTypeActeur(formData.type_acteur);
                        if (!t) return null;
                        const I = t.Icon;
                        return (
                          <span className="flex items-center gap-2">
                            <I size={16} weight="fill" style={{ color: t.color }} />
                            <span>{t.label}</span>
                          </span>
                        );
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {typeActeurOptions.map(t => {
                      const I = t.Icon;
                      return (
                        <SelectItem key={t.value} value={t.value} data-testid={`type-acteur-opt-${t.value}`}>
                          <span className="flex items-center gap-2">
                            <I size={16} weight="fill" style={{ color: t.color }} />
                            <span>{t.label}</span>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="statut_relation">Statut relation</Label>
                <Select value={formData.statut_relation} onValueChange={(v) => setFormData({ ...formData, statut_relation: v })}>
                  <SelectTrigger data-testid="select-statut">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statutRelationOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Adresse */}
            <div className="border-t border-[#E8E6E1] pt-4 mt-2">
              <p className="text-xs font-semibold text-[#5E6D66] uppercase tracking-wider mb-3">Adresse</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="adresse">N° et rue</Label>
                    <Input id="adresse" value={formData.adresse} onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} placeholder="12 rue des Flamboyants" data-testid="input-contact-adresse-rue" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adresse_quartier">Quartier / Lieu-dit</Label>
                    <Input id="adresse_quartier" value={formData.adresse_quartier} onChange={(e) => setFormData({ ...formData, adresse_quartier: e.target.value })} placeholder="Quartier Fonds-Saint-Denis" data-testid="input-contact-adresse-quartier" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="adresse_batiment">Bâtiment / Résidence</Label>
                    <Input id="adresse_batiment" value={formData.adresse_batiment} onChange={(e) => setFormData({ ...formData, adresse_batiment: e.target.value })} placeholder="Bât. B" data-testid="input-contact-adresse-batiment" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adresse_porte">Porte / Appartement / Bureau</Label>
                    <Input id="adresse_porte" value={formData.adresse_porte} onChange={(e) => setFormData({ ...formData, adresse_porte: e.target.value })} placeholder="Porte 12" data-testid="input-contact-adresse-porte" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code_postal">Code postal</Label>
                    <Input id="code_postal" value={formData.code_postal} maxLength={5} onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })} placeholder="97200" data-testid="input-contact-code-postal" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ville">Ville</Label>
                    <Input id="ville" value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} placeholder="Fort-de-France" data-testid="input-contact-ville" />
                  </div>
                </div>
              </div>
            </div>

            {/* Structure représentée */}
            <fieldset
              disabled={structureDisabled}
              aria-disabled={structureDisabled}
              aria-labelledby="contact-structure-title"
              aria-describedby={structureDisabled ? 'contact-structure-disabled-note' : undefined}
              data-testid="contact-structure-section"
              className={`min-w-0 border-t border-[#E8E6E1] pt-4 mt-2 transition-colors ${structureDisabled ? 'bg-gray-100 grayscale opacity-60' : ''}`}
            >
              <p id="contact-structure-title" data-testid="contact-structure-title" className="text-xs font-semibold text-[#5E6D66] uppercase tracking-wider mb-3">Structure représentée</p>
              {structureDisabled && <p id="contact-structure-disabled-note" data-testid="contact-structure-disabled-note" className="text-xs text-[#5E6D66] mb-3">Non applicable — personne physique</p>}
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="raison_sociale">Raison sociale</Label>
                    <Input
                      id="raison_sociale"
                      disabled={structureDisabled}
                      value={formData.raison_sociale}
                      onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                      placeholder="Ex : SCIC Solidaire Antilles"
                      data-testid="input-raison-sociale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="statut_juridique">Statut juridique</Label>
                    <Select disabled={structureDisabled} value={formData.statut_juridique} onValueChange={(v) => setFormData(prev => ({
                      ...prev,
                      statut_juridique: v,
                      // Reset le type de collectivité si le statut ne l'exige plus
                      type_collectivite: (v === 'collectivite' || v === 'epci') ? prev.type_collectivite : '',
                    }))}>
                      <SelectTrigger data-testid="select-statut-juridique">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[260px]">
                        {statutJuridiqueOptions.map(s => (
                          <SelectItem key={s.value} value={s.value} data-testid={`statut-juridique-opt-${s.value}`}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[180px_minmax(0,1fr)] gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="immatriculation_type">Immatriculation</Label>
                    <Select disabled={structureDisabled} value={formData.immatriculation_type} onValueChange={(v) => setFormData({ ...formData, immatriculation_type: v })}>
                      <SelectTrigger data-testid="select-immatriculation-type">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {immatriculationTypeOptions.map(o => (
                          <SelectItem key={o.value} value={o.value} data-testid={`immat-opt-${o.value}`}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="immatriculation_numero">Numéro</Label>
                    <div className="flex gap-2">
                      <Input
                        id="immatriculation_numero"
                        disabled={structureDisabled}
                        value={formData.immatriculation_numero}
                        onChange={(e) => setFormData({ ...formData, immatriculation_numero: e.target.value })}
                        placeholder={
                          formData.immatriculation_type === 'siret' ? '14 chiffres' :
                          formData.immatriculation_type === 'ridet' ? '9 chiffres' : 'Référence'
                        }
                        data-testid="input-immatriculation-numero"
                      />
                      {formData.immatriculation_type === 'siret' && (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={structureDisabled || sireneState.loading || !isValidSiretLuhn(formData.immatriculation_numero)}
                          onClick={() => triggerSireneLookup({ silent: false })}
                          data-testid="btn-sirene-lookup"
                          className="whitespace-nowrap"
                          title="Rechercher dans la base SIRENE (INSEE)"
                        >
                          {sireneState.loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MagnifyingGlass size={16} weight="bold" />
                          )}
                          <span className="ml-2 hidden sm:inline">SIRENE</span>
                        </Button>
                      )}
                    </div>
                    {formData.immatriculation_type === 'siret' && sireneState.message && (
                      <p
                        data-testid="sirene-status"
                        className={
                          'text-xs ' +
                          (sireneState.tone === 'error' ? 'text-red-600' :
                           sireneState.tone === 'warning' ? 'text-amber-700' :
                           sireneState.tone === 'success' ? 'text-emerald-700' :
                           'text-[#5E6D66]')
                        }
                      >
                        {sireneState.message}
                      </p>
                    )}
                  </div>
                </div>
                {(formData.statut_juridique === 'collectivite' || formData.statut_juridique === 'epci') && (
                  <div className="space-y-2">
                    <Label htmlFor="type_collectivite">Type de collectivité</Label>
                    <Select disabled={structureDisabled} value={formData.type_collectivite} onValueChange={(v) => setFormData({ ...formData, type_collectivite: v })}>
                      <SelectTrigger data-testid="select-type-collectivite">
                        <SelectValue placeholder="Sélectionner…" />
                      </SelectTrigger>
                      <SelectContent>
                        {typeCollectiviteOptions.map(o => (
                          <SelectItem key={o.value} value={o.value} data-testid={`type-coll-opt-${o.value}`}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Activité(s) / secteur(s)</Label>
                  <MultiSelectChips
                    disabled={structureDisabled}
                    options={SECTEURS}
                    values={formData.secteurs || []}
                    onChange={(vals) => setFormData({ ...formData, secteurs: vals })}
                    placeholder="Choisir un ou plusieurs secteurs…"
                    testId="secteurs"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code NAF/APE officiel (INSEE)</Label>
                  <NafAutocomplete
                    disabled={structureDisabled}
                    value={formData.code_naf ? { code: formData.code_naf, label: formData.naf_label } : null}
                    onChange={(v) => setFormData({ ...formData, code_naf: v?.code || '', naf_label: v?.label || '' })}
                    testId="naf"
                  />
                </div>
              </div>
            </fieldset>

            {/* Custom Fields */}
            {customFieldDefs.length > 0 && (
              <div className="border-t border-[#E8E6E1] pt-4 mt-2">
                <p className="text-xs font-semibold text-[#5E6D66] uppercase tracking-wider mb-3">Champs personnalises</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {customFieldDefs.map((field) => {
                    const val = formData.custom_fields?.[field.id] ?? '';
                    const onChange = (newVal) => setFormData(prev => ({
                      ...prev,
                      custom_fields: { ...prev.custom_fields, [field.id]: newVal }
                    }));
                    return (
                      <div key={field.id} className={`space-y-2 ${field.field_type === 'textarea' ? 'col-span-full' : ''}`}>
                        <Label htmlFor={`cf-${field.id}`}>
                          {field.label}{field.required ? ' *' : ''}
                        </Label>
                        {field.field_type === 'text' && (
                          <Input
                            id={`cf-${field.id}`}
                            value={val}
                            onChange={(e) => onChange(e.target.value)}
                            required={field.required}
                            data-testid={`cf-${field.name}`}
                          />
                        )}
                        {field.field_type === 'number' && (
                          <Input
                            id={`cf-${field.id}`}
                            type="number"
                            value={val}
                            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
                            required={field.required}
                            data-testid={`cf-${field.name}`}
                          />
                        )}
                        {field.field_type === 'date' && (
                          <Input
                            id={`cf-${field.id}`}
                            type="date"
                            value={val}
                            onChange={(e) => onChange(e.target.value)}
                            required={field.required}
                            data-testid={`cf-${field.name}`}
                          />
                        )}
                        {field.field_type === 'select' && (
                          <Select value={val || ''} onValueChange={onChange}>
                            <SelectTrigger data-testid={`cf-${field.name}`}>
                              <SelectValue placeholder="Selectionner..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(field.options || []).map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {field.field_type === 'checkbox' && (
                          <div className="flex items-center gap-2 pt-1">
                            <Checkbox
                              id={`cf-${field.id}`}
                              checked={!!val}
                              onCheckedChange={(checked) => onChange(checked)}
                              data-testid={`cf-${field.name}`}
                            />
                            <label htmlFor={`cf-${field.id}`} className="text-sm text-[#5E6D66]">Oui</label>
                          </div>
                        )}
                        {field.field_type === 'textarea' && (
                          <Textarea
                            id={`cf-${field.id}`}
                            value={val}
                            onChange={(e) => onChange(e.target.value)}
                            required={field.required}
                            rows={3}
                            data-testid={`cf-${field.name}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            </fieldset>
            {saveError && <p role="alert" className="break-words text-sm text-red-700" data-testid="contact-save-error">{saveError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" disabled={savingContact} onClick={() => setDialogOpen(false)} data-testid="cancel-contact-save">
                Annuler
              </Button>
              <Button type="submit" disabled={savingContact} className="min-w-[132px] bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-contact-btn">
                {savingContact && <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />}
                <span role="status" data-testid="contact-save-button-label">{savingContact ? 'Enregistrement…' : editingContact ? 'Mettre à jour' : 'Créer'}</span>
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Attachments Dialog */}
      <Dialog open={attachDialog.open} onOpenChange={(v) => setAttachDialog({ open: v, contact: v ? attachDialog.contact : null })}>
        <DialogContent className="max-w-xl" data-testid="attachments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Paperclip size={18} className="text-[#2D5A4C]" />
              Pieces jointes — {attachDialog.contact?.prenom} {attachDialog.contact?.nom}
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>

          {/* Upload section */}
          <div className="p-3 rounded-lg border border-dashed border-[#E8E6E1] space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <Select value={attachType} onValueChange={setAttachType}>
                <SelectTrigger className="text-xs" data-testid="attach-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cv">CV</SelectItem>
                  <SelectItem value="piece_identite">Piece identite</SelectItem>
                  <SelectItem value="rib">RIB</SelectItem>
                  <SelectItem value="contrat">Contrat</SelectItem>
                  <SelectItem value="attestation">Attestation</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={attachDesc}
                onChange={(e) => setAttachDesc(e.target.value)}
                placeholder="Description (optionnel)"
                className="text-xs col-span-2"
                data-testid="attach-description-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" onChange={handleFileUpload} className="hidden" data-testid="attach-file-input" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="border-[#2D5A4C] text-[#2D5A4C]"
                data-testid="attach-upload-btn"
              >
                {uploadingFile ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <UploadSimple size={14} className="mr-1.5" />}
                Ajouter un fichier
              </Button>
              <span className="text-[10px] text-[#5E6D66]">Max 10 Mo</span>
            </div>
          </div>

          {/* Attachments list */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto" data-testid="attachments-list">
            {attachLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" /></div>
            ) : attachments.length === 0 ? (
              <div className="text-center py-8">
                <FileText size={24} className="mx-auto text-[#E8E6E1] mb-2" />
                <p className="text-sm text-[#5E6D66]">Aucune piece jointe</p>
              </div>
            ) : (
              attachments.map((att) => {
                const size = att.size > 1024 * 1024 ? `${(att.size / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(att.size / 1024)} Ko`;
                return (
                  <div key={att.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#F9F8F5] hover:bg-[#F0EFEB] transition-colors" data-testid={`attachment-${att.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center">
                      <FileText size={16} className="text-[#2D5A4C]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#1A2421] truncate">{att.original_filename}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[9px]">{pieceTypeLabels[att.type_piece] || att.type_piece}</Badge>
                        <span className="text-[10px] text-[#5E6D66]">{size}</span>
                        {att.description && <span className="text-[10px] text-[#5E6D66] truncate max-w-[120px]">{att.description}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#00AEEF]" onClick={() => handleDownloadAttachment(att)} data-testid={`download-att-${att.id}`}>
                        <DownloadSimple size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDeleteAttachment(att)} data-testid={`delete-att-${att.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachDialog({ open: false, contact: null })}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Contact Dialog */}
      <Dialog open={duplicateDialog.open} onOpenChange={(v) => !v && setDuplicateDialog({ open: false, dups: [], pending: null })}>
        <DialogContent className="max-w-xl" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Warning size={20} className="text-[#E8A365]" weight="fill" />
              Contact déjà existant
            </DialogTitle>
            <DialogDescription>
              Un ou plusieurs contacts avec un email ou un téléphone similaire existent déjà. Vous pouvez ouvrir un existant, fusionner vos infos dans celui-ci (les champs vides seront complétés sans écraser les valeurs existantes), ou créer quand même.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="duplicate-list">
            {duplicateDialog.dups.map((d) => (
              <div key={d.id} className="p-3 rounded-lg border border-[#E8E6E1] hover:border-[#2D5A4C] transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#1A2421]">{d.prenom} {d.nom}</div>
                    <div className="text-xs text-[#5E6D66] flex flex-wrap gap-3 mt-1">
                      <span className="flex items-center gap-1"><EnvelopeSimple size={12} />{d.email}</span>
                      {d.telephone && <span className="flex items-center gap-1"><Phone size={12} />{d.telephone_indicatif || ''} {d.telephone}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setDuplicateDialog({ open: false, dups: [], pending: null });
                        setDialogOpen(false);
                        navigate(`/contacts/${d.id}`);
                      }}
                      data-testid={`duplicate-open-${d.id}`}
                    >
                      Ouvrir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => duplicateDialog.pending && submitMerge(d.id, duplicateDialog.pending)}
                      data-testid={`duplicate-merge-auto-${d.id}`}
                      title="Compléter les champs vides automatiquement"
                    >
                      Fusion auto
                    </Button>
                    <Button
                      size="sm"
                      className="bg-[#2D5A4C] hover:bg-[#1E4136]"
                      onClick={() => duplicateDialog.pending && openMergeDialog(d.id, duplicateDialog.pending)}
                      data-testid={`duplicate-merge-${d.id}`}
                      title="Choisir champ par champ"
                    >
                      Fusionner
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialog({ open: false, dups: [], pending: null })} data-testid="duplicate-cancel-btn">
              Annuler
            </Button>
            <Button
              variant="outline"
              className="border-[#E8A365] text-[#E8A365] hover:bg-[#E8A365]/10"
              onClick={() => duplicateDialog.pending && submitCreate(duplicateDialog.pending, { force: true })}
              data-testid="duplicate-force-btn"
            >
              Créer quand même
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bidirectional Merge Dialog */}
      <Dialog open={mergeDialog.open} onOpenChange={(v) => !v && setMergeDialog({ open: false, existing: null, incoming: null, decisions: {} })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="merge-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Fusion personnalisée</DialogTitle>
            <DialogDescription>
              Pour chaque champ, choisissez la valeur à conserver dans le contact existant.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const next = {};
                MERGE_FIELDS.forEach(f => { next[f.key] = 'existing'; });
                setMergeDialog(prev => ({ ...prev, decisions: next }));
              }}
              data-testid="merge-all-existing"
            >
              Tout garder de l'existant
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const next = {};
                MERGE_FIELDS.forEach(f => {
                  const iv = mergeDialog.incoming?.[f.key];
                  next[f.key] = !isEmpty(iv) ? 'new' : 'existing';
                });
                setMergeDialog(prev => ({ ...prev, decisions: next }));
              }}
              data-testid="merge-all-new"
            >
              Tout prendre du nouveau
            </Button>
          </div>

          <div className="border border-[#E8E6E1] rounded-lg overflow-hidden">
            <div className="grid grid-cols-[130px_1fr_1fr] bg-[#F9F8F5] border-b border-[#E8E6E1] px-3 py-2 text-xs uppercase font-semibold text-[#5E6D66]">
              <div>Champ</div>
              <div>Existant</div>
              <div>Nouveau (saisi)</div>
            </div>
            {mergeDialog.existing && mergeDialog.incoming && MERGE_FIELDS.map(f => {
              const ev = mergeDialog.existing[f.key];
              const iv = mergeDialog.incoming[f.key];
              const changed = fmtMergeValue(ev) !== fmtMergeValue(iv);
              const decision = mergeDialog.decisions[f.key] || 'existing';
              const setDec = (val) => setMergeDialog(prev => ({ ...prev, decisions: { ...prev.decisions, [f.key]: val } }));
              return (
                <div key={f.key} className={`grid grid-cols-[130px_1fr_1fr] px-3 py-2 border-b border-[#F0EFEB] text-sm ${changed ? 'bg-white' : 'bg-[#F9F8F5]/40 opacity-70'}`} data-testid={`merge-row-${f.key}`}>
                  <div className="text-[#5E6D66] pt-1.5">{f.label}</div>
                  <label className={`flex items-start gap-2 p-2 rounded cursor-pointer ${decision === 'existing' ? 'bg-[#2D5A4C]/10 border border-[#2D5A4C]' : 'border border-transparent hover:border-[#E8E6E1]'}`}>
                    <input
                      type="radio"
                      name={`merge-${f.key}`}
                      checked={decision === 'existing'}
                      onChange={() => setDec('existing')}
                      className="mt-1"
                      data-testid={`merge-${f.key}-existing`}
                    />
                    <span className="text-[#1A2421] break-words">{fmtMergeValue(ev)}</span>
                  </label>
                  <label className={`flex items-start gap-2 p-2 rounded cursor-pointer ${decision === 'new' ? 'bg-[#E8A365]/10 border border-[#E8A365]' : 'border border-transparent hover:border-[#E8E6E1]'} ${isEmpty(iv) ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name={`merge-${f.key}`}
                      checked={decision === 'new'}
                      onChange={() => !isEmpty(iv) && setDec('new')}
                      disabled={isEmpty(iv)}
                      className="mt-1"
                      data-testid={`merge-${f.key}-new`}
                    />
                    <span className="text-[#1A2421] break-words">{fmtMergeValue(iv)}</span>
                  </label>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialog({ open: false, existing: null, incoming: null, decisions: {} })} data-testid="merge-cancel-btn">
              Annuler
            </Button>
            <Button className="bg-[#2D5A4C] hover:bg-[#1E4136]" onClick={applyMergeDecisions} data-testid="merge-apply-btn">
              Appliquer la fusion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Excel Dialog */}
      <Dialog open={importDialog} onOpenChange={(v) => { setImportDialog(v); if (!v) setImportResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <FileXls size={20} className="text-[#2D5A4C]" weight="fill" />
              Importer des contacts depuis Excel
            </DialogTitle>
            <DialogDescription>
              Téléchargez le template, remplissez-le puis importez. La normalisation nom/prénom et la détection des doublons sont appliquées.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-[#F9F8F5] border border-[#E8E6E1]">
              <p className="text-sm text-[#1A2421] font-medium mb-2">1. Téléchargez le template</p>
              <Button variant="outline" size="sm" onClick={handleImportTemplate} data-testid="download-template-btn">
                <DownloadSimple size={14} className="mr-2" />
                Télécharger le template Excel
              </Button>
            </div>
            <div className="p-3 rounded-lg bg-[#F9F8F5] border border-[#E8E6E1]">
              <p className="text-sm text-[#1A2421] font-medium mb-2">2. Importez le fichier rempli</p>
              <input ref={importFileRef} type="file" accept=".xlsx,.xls" onChange={handleImportSubmit} className="hidden" data-testid="import-file-input" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => importFileRef.current?.click()}
                disabled={importing}
                data-testid="import-upload-btn"
              >
                {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <UploadSimple size={14} className="mr-2" />}
                Choisir le fichier .xlsx
              </Button>
            </div>
            {importResult && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="import-result">
                <div className="font-medium text-emerald-800">Import terminé</div>
                <div className="text-emerald-700 mt-1">
                  <div>✓ {importResult.inserted} contact(s) importé(s)</div>
                  <div>↷ {importResult.skipped} contact(s) ignoré(s) (doublons)</div>
                  {importResult.errors?.length > 0 && (
                    <div className="text-red-700 mt-2">
                      {importResult.errors.length} erreur(s) : {importResult.errors.slice(0, 3).map(e => `L${e.row}: ${e.error}`).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportDialog(false); setImportResult(null); }} data-testid="import-close-btn">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
